import React, { useEffect, useState } from 'react';
import { Card, Tag, Space, List, Spin, Typography, Empty } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import OnboardingService, { OnboardingRequirements } from '../services/onboarding';

const { Text } = Typography;

// 后端返回的字段 key → 中文标签
const fieldLabels: Record<string, string> = {
  email: '邮箱',
  business_type: '业务类型',
  first_name: '名',
  last_name: '姓',
  dob: '出生日期',
  address: '地址',
  phone: '电话',
  ssn_last_4: '社保号后四位',
  business_name: '企业名称',
  business_tax_id: '企业税号',
  mcc: '商户类别码（MCC）',
  name: '姓名',
  id_number: '证件号码',
};

const documentLabels: Record<string, string> = {
  identity_document: '身份证件',
  address_verification: '地址证明',
  id_card_front: '身份证正面',
  id_card_back: '身份证背面',
  face_photo: '人脸照片',
  business_license: '营业执照',
  articles_of_incorporation: '公司章程',
  organization_code: '组织机构代码',
  tax_registration: '税务登记证',
  legal_person_id_front: '法人身份证正面',
  legal_person_id_back: '法人身份证背面',
};

const labelOf = (key: string, map: Record<string, string>) => map[key] || key;

interface Props {
  plugin?: string;
}

const OnboardingRequirementsCard: React.FC<Props> = ({ plugin }) => {
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState<OnboardingRequirements | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    OnboardingService.getOnboardingRequirements(plugin)
      .then((data) => {
        if (mounted) setRequirements(data);
      })
      .catch(() => {
        if (mounted) setRequirements(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [plugin]);

  const hasContent =
    requirements &&
    ((requirements.required_fields && requirements.required_fields.length > 0) ||
      (requirements.required_documents && requirements.required_documents.length > 0) ||
      (requirements.verification_steps && requirements.verification_steps.length > 0));

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined style={{ color: '#2F6BFF' }} />
          <span>入驻要求</span>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : !hasContent ? (
        <Empty description="暂无入驻要求信息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {requirements?.required_fields && requirements.required_fields.length > 0 && (
            <div>
              <Text strong>必填资料</Text>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {requirements.required_fields.map((f) => (
                    <Tag key={f} color="blue">
                      {labelOf(f, fieldLabels)}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          )}

          {requirements?.required_documents && requirements.required_documents.length > 0 && (
            <div>
              <Text strong>需上传证件</Text>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {requirements.required_documents.map((d) => (
                    <Tag key={d} color="orange">
                      {labelOf(d, documentLabels)}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          )}

          {requirements?.verification_steps && requirements.verification_steps.length > 0 && (
            <div>
              <Text strong>验证环节</Text>
              <List
                size="small"
                style={{ marginTop: 8 }}
                dataSource={requirements.verification_steps}
                renderItem={(step) => (
                  <List.Item>
                    <Text>{step.description || step.name}</Text>
                  </List.Item>
                )}
              />
            </div>
          )}
        </Space>
      )}
    </Card>
  );
};

export default OnboardingRequirementsCard;
